/*
 * Acta imprimible de una visita IGI. Mismo contenido y las mismas 3 firmas que el IGI original
 * (C:\igi\index.html, printVisita), pero como React normal (todo el texto queda escapado; el
 * original armaba el HTML a mano con document.write, con riesgo XSS que no se porta aqui) dentro
 * del componente Modal que ya existe. Imprime la nota VIGENTE de la tienda al momento de imprimir
 * (llega por props desde tienda/index.js, no se vuelve a pedir). Solo aparece con una visita ya
 * guardada (con id): "Imprimir acta" en visitas.js siempre manda una visita guardada.
 * Mientras el acta esta montada se agrega `imprimiendo-acta` al <body> (se quita al desmontar): las
 * reglas @media print de acta-visita.css quedan condicionadas a esa clase para no afectar el Ctrl+P
 * del resto de la app (acta-visita.css se importa directo, sin code-splitting por ruta, asi que su
 * CSS es global). El modal se muestra con ancho responsivo (95% hasta 800px) para no forzar scroll
 * horizontal en celular.
 * Diseño calcado del acta real de produccion (PDF de referencia "Acta de Visita - Faraon", 2026-09-30):
 * franja de marca arriba, grilla de datos, pastilla de color para el IGI (mismo esquema de 3 colores
 * que ya usan Retro/ERP, ver estilo3/nivel3 en niveles.js), encabezados de tabla oscuros y barras de
 * seccion para "Acciones acordadas"/"Compromisos". No hay dato de "Representante de la Joyeria" en
 * este sistema (Establishments no tiene encargado) - esa firma queda con la linea vacia a proposito,
 * a diferencia del PDF de referencia que si trae ese nombre.
 */
import React, {useEffect} from "react";
import Modal from "../../../../components/modal";
import Button from "../../../../components/button";
import {formatoFecha, formatoNota, nivel3} from "../niveles";
import "./acta-visita.css";

const hoy = () => {
    const ahora = new Date();
    const mes = String(ahora.getMonth() + 1).padStart(2, "0");
    const dia = String(ahora.getDate()).padStart(2, "0");
    return `${ahora.getFullYear()}-${mes}-${dia}`;
};

// Punto de color de la pastilla del IGI: mismo esquema de 3 colores que Retro/ERP (nivel3), no el de
// 5 niveles - el texto de la pastilla si usa la palabra de 5 niveles que ya trae evaluacionVigente.nivel
// (p.ej. "🟡 3.76 — Estable", calcado del PDF de referencia).
const PUNTO_NIVEL = {fuerte: "🟢", media: "🟡", debil: "🔴"};

const CHECKLIST = [
    ["visito", "Visitó la joyería"],
    ["reviso_resultados", "Revisó resultados"],
    ["ejecuto_activacion", "Ejecutó activación comercial"],
    ["feedback", "Dio feedback puntual"],
    ["reconocio", "Reconoció a un colaborador"],
    ["detecto_brecha", "Detectó brecha concreta"],
    ["dejo_tareas", "Dejó tareas escritas"],
    ["pidio_evidencia", "Pidió evidencia"],
    ["seguimiento_ant", "Hizo seguimiento a compromisos anteriores"],
];

const ActaVisita = ({visita, tienda, evaluacionVigente, onClose}) => {
    useEffect(() => {
        if (!visita) {
            return undefined;
        }
        document.body.classList.add("imprimiendo-acta");
        return () => document.body.classList.remove("imprimiendo-acta");
    }, [visita]);

    if (!visita) {
        return null;
    }
    const ritualConTexto = Object.values(visita.ritual).some((v) => v);
    return (
        <Modal
            show
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="modal-lg"
            style={{maxWidth: "800px", width: "95%"}}
        >
            <div className="acta-imprimible">
                <div className="acta-marca">
                    <div>
                        <div className="acta-marca-nombre">OROCASH</div>
                        <div className="acta-marca-sub">Inventario &amp; Gestión Interna</div>
                    </div>
                    <div className="text-right">
                        <div className="acta-marca-titulo">ACTA DE VISITA A JOYERÍA</div>
                        <div className="acta-marca-sub">Plataforma IGI · Uso interno</div>
                    </div>
                </div>

                <h5 className="acta-registro-titulo">
                    <i className="bx bx-file"/> Registro de Visita — {tienda ? tienda.nombre : ""}
                </h5>

                <div className="acta-info-grid">
                    <div>
                        <div className="acta-info-label">Agencia / Joyería</div>
                        <strong>{tienda ? `${tienda.nombre} (${tienda.codigo})` : "—"}</strong>
                    </div>
                    <div>
                        <div className="acta-info-label">Zona</div>
                        <strong>{tienda ? (tienda.zona || "—") : "—"}</strong>
                    </div>
                    <div>
                        <div className="acta-info-label">Jefe zonal</div>
                        <strong>{visita.autor.nombres || visita.autor.usuario}</strong>
                    </div>
                    <div>
                        <div className="acta-info-label">Fecha de visita</div>
                        <strong>{formatoFecha(visita.fecha)}</strong>
                    </div>
                    <div>
                        <div className="acta-info-label">Horario</div>
                        <strong>{visita.hora_inicio || "—"}{visita.hora_fin ? ` – ${visita.hora_fin}` : ""}</strong>
                    </div>
                    {evaluacionVigente && (
                        <div className="acta-info-igi">
                            <div className="acta-info-label">IGI actual</div>
                            <span className={`acta-igi-pill acta-igi-${nivel3(evaluacionVigente.igi) || "sin-dato"}`}>
                                {PUNTO_NIVEL[nivel3(evaluacionVigente.igi)] || "⬜"} {formatoNota(evaluacionVigente.igi)} — {evaluacionVigente.nivel}
                            </span>
                        </div>
                    )}
                </div>

                <h6 className="acta-seccion acta-seccion-antes"><i className="bx bx-pin"/> ANTES — Objetivo de la visita</h6>
                <div className="small text-muted">¿Qué iba a revisar / lograr?</div>
                <p className="acta-caja" style={{whiteSpace: "pre-wrap"}}>
                    {visita.objetivo || "No registrado"}
                </p>

                <h6 className="acta-seccion acta-seccion-durante"><i className="bx bx-search"/> DURANTE — Diagnóstico en terreno</h6>
                <div className="row">
                    <div className="col-6">
                        <div className="small text-muted">¿Qué está pasando?</div>
                        <p className="acta-caja" style={{whiteSpace: "pre-wrap"}}>
                            {visita.que_pasa || "No registrado"}
                        </p>
                    </div>
                    <div className="col-6">
                        <div className="small text-muted">¿Cuál es el desafío?</div>
                        <p className="acta-caja" style={{whiteSpace: "pre-wrap"}}>
                            {visita.desafio || "No registrado"}
                        </p>
                    </div>
                </div>
                <div className="acta-seccion acta-seccion-decision"><i className="bx bx-check-circle"/> Decisión tomada</div>
                <p className="acta-caja" style={{whiteSpace: "pre-wrap"}}>
                    {visita.decision || "No registrado"}
                </p>

                <div className="acta-lista-titulo">Lista de verificación</div>
                <table className="table table-sm table-striped acta-tabla mb-1">
                    <thead><tr><th>Acción ejecutada</th><th className="text-center">Estado</th></tr></thead>
                    <tbody>
                        {CHECKLIST.map(([clave, etiqueta]) => (
                            <tr key={clave}>
                                <td>{etiqueta}</td>
                                <td className="text-center">
                                    <span className={visita.checklist[clave] ? "acta-check-si" : "acta-check-no"}>
                                        {visita.checklist[clave] ? "✔" : ""}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {visita.acciones.length > 0 && (
                    <>
                        <h6 className="acta-barra-titulo"><i className="bx bx-clipboard"/> Acciones acordadas</h6>
                        <table className="table table-sm table-striped acta-tabla mb-1">
                            <thead>
                                <tr><th>#</th><th>Tema</th><th>Acción</th><th>Responsable</th><th>Fecha</th><th>Estado</th></tr>
                            </thead>
                            <tbody>
                                {visita.acciones.map((a, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td>{a.tema || "—"}</td>
                                        <td>{a.accion || "—"}</td>
                                        <td>{a.responsable || "—"}</td>
                                        <td>{a.fecha ? formatoFecha(a.fecha) : "—"}</td>
                                        <td>{a.estado}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {visita.compromisos.length > 0 && (
                    <>
                        <h6 className="acta-barra-titulo"><i className="bx bx-handshake"/> Compromisos</h6>
                        <table className="table table-sm table-striped acta-tabla mb-1">
                            <thead><tr><th>#</th><th>Compromiso</th><th>Acción definida</th><th>Estado</th></tr></thead>
                            <tbody>
                                {visita.compromisos.map((c, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td>{c.compromiso || "—"}</td>
                                        <td>{c.accion || "—"}</td>
                                        <td>{c.estado}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {visita.reflexion && (
                    <>
                        <h6 className="acta-barra-titulo">Reflexión del jefe zonal</h6>
                        <p className="acta-caja" style={{whiteSpace: "pre-wrap"}}>{visita.reflexion}</p>
                    </>
                )}

                {ritualConTexto && (
                    <>
                        <h6 className="acta-barra-titulo">Ritual de seguimiento (15 min)</h6>
                        {visita.ritual.semana && <p className="mb-50"><strong>Semana:</strong> {visita.ritual.semana}</p>}
                        {visita.ritual.dijimos && <p className="mb-50"><strong>1. ¿Qué dijimos que íbamos a hacer?</strong> {visita.ritual.dijimos}</p>}
                        {visita.ritual.se_hizo && <p className="mb-50"><strong>2. ¿Qué sí se hizo?</strong> {visita.ritual.se_hizo}</p>}
                        {visita.ritual.aprendimos && <p className="mb-50"><strong>3. ¿Qué aprendimos?</strong> {visita.ritual.aprendimos}</p>}
                        {visita.ritual.acuerdos && <p className="mb-50"><strong>Acuerdos próxima semana:</strong> {visita.ritual.acuerdos}</p>}
                    </>
                )}

                <div className="row mt-2">
                    <div className="col-4 text-center">
                        <div className="acta-firma-linea"/>
                        <div className="small">Jefe Zonal</div>
                        <div className="small text-muted">{visita.autor.nombres || visita.autor.usuario}</div>
                    </div>
                    <div className="col-4 text-center">
                        <div className="acta-firma-linea"/>
                        <div className="small">Representante de la Joyería</div>
                    </div>
                    <div className="col-4 text-center">
                        <div className="acta-firma-linea"/>
                        <div className="small">Fecha y hora de entrega</div>
                    </div>
                </div>
                <p className="acta-pie mt-1">
                    Este documento es de uso interno. Generado desde Ingresos el {formatoFecha(hoy())}.
                </p>

                <div className="acta-no-imprimir d-flex justify-content-center mt-1" style={{gap: "0.5rem"}}>
                    <Button type="button" theme="primary" text={<><i className="bx bx-printer"/> Imprimir / Guardar PDF</>}
                        onChange={() => window.print()}/>
                    <Button type="button" theme="light" text="Cerrar" onChange={onClose}/>
                </div>
            </div>
        </Modal>
    );
};

export default ActaVisita;
